import { useTikTokReadMarker } from 'src/pages/TikTok/TikTok.actions';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { leadCloseChatEvents } from 'src/components/LeadClose/LeadClose.functions';
import LeadCloseSuggestion from 'src/components/LeadClose/LeadCloseSuggestion.component';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { orderBy, uniqBy } from 'lodash';
import {
  createHeldBlobUrl,
  releaseBlobUrlHolder,
} from 'src/utils/blobUrlRegistry';
import { retainDraftBlobAttachments } from '../MessageBar/draftMessages.store';
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Loading, Skeleton } from 'react-daisyui';
import { AgendaService } from '../../../../pages/Agenda/Agenda.service.ts';
import { getDuration } from '../../../../pages/Agenda/components/Calendar/Calendar.functions.ts';
import { useLayoutContext } from '../../../../providers/LayoutProvider/Layout.context.ts';
import { useModalsContext } from '../../../../providers/ModalsProvider/Modals.context.ts';
import { useSocketsContext } from '../../../../providers/SocketsProvider/Sockets.context.ts';
import {
  eChannelType,
  eChatEventTypes,
} from '../../../../providers/SocketsProvider/Sockets.enums.ts';
import {
  tChatContactEvent,
  tChatEvent as tChatEventSocket,
  tReactionSocket,
} from '../../../../providers/SocketsProvider/Sockets.types.ts';
import { eAlertType } from '../../../../providers/ToastProvider/Toast.enum.ts';
import { useToastContext } from '../../../../providers/ToastProvider/Toast.hook.ts';
import useChatActions from '../../Chat.actions.ts';
import { ChatMessageContainerEnum, EventType } from '../../Chat.enum.ts';
import {
  convertDurationToMinutes,
  convertToCalendarEventType,
  mergeByHandle,
} from '../../Chat.functions.ts';
import { useChatContext } from '../../Chat.hook.ts';
import { ChatService } from '../../Chat.service.ts';
import {
  tAttachment,
  tCalendarEvent,
  tChatAttachment,
  tContent,
  tMessage,
  tParticipant,
} from '../../Chat.types.ts';
import { MediaCompressionService } from 'src/services/MediaCompression/MediaCompression.service';
import chatUtils from '../../Chat.utils.ts';
import { generateVideoPoster } from '../MessageBar/components/Assets/Assets.functions.ts';
import { isVideo } from '../MessageBar/components/Thumbnail/Thumbnail.functions.ts';
import {
  getMaxFileSizeForChannel,
  isAcceptableForChannel,
  isEmptyFile,
} from '../MessageBar/components/ToolActions/components/DriveFilesAction/components/UploadFileAction/UploadFileAction.constants.ts';
import EventBubble from './components/EventBubble/EventBubble.component.tsx';
import EventBubbleProvider from './components/EventBubble/EventBubble.context.tsx';
import AlbumBubble from './components/EventBubble/components/AlbumBubble/AlbumBubble.component.tsx';
import { groupChatAlbums } from './components/EventBubble/components/AlbumBubble/AlbumBubble.functions.ts';
import { pairScheduledMessages } from './components/EventBubble/components/FollowUpBubble/FollowUpBubble.functions.ts';
import {
  VITE_CHAT_ALBUM_INBOUND_GAP_SECONDS,
  VITE_CHAT_ALBUM_OUTBOUND_GAP_SECONDS,
} from '../../../../constants/envs.ts';
import Records from './components/Records/Records.component.tsx';
import {
  buildChatEventFromSocketEvent,
  buildDecryptedAttachment,
  buildEventContent,
  createInFlightGuard,
} from './MessageContainer.functions.ts';
import {
  classifyAnchorLoadError,
  createEventNodeRegistry,
  focusChatEvent,
  prefersReducedMotion,
  windowContainsAnchor,
} from './MessageContainer.focus.ts';
import ScrollButton from './components/ScrollButton/ScrollButton.component.tsx';
import DaySeparator from './components/DaySeparator/DaySeparator.component.tsx';
import FloatingDayChip from './components/DaySeparator/FloatingDayChip.component.tsx';
import {
  buildBubbleLayout,
  orderThreadEvents,
} from './bubbleLayout.functions.ts';
import useBubbleDate from './bubbleDate/useBubbleDate.hook.ts';
import BotThinkingBubble from './components/BotThinkingBubble/BotThinkingBubble.component.tsx';
import useLeadAgentIndicator from './components/BotThinkingBubble/useLeadAgentIndicator.hook.ts';
import useChatSearchHighlight from '../ChatSearch/ChatSearchHighlight.hook.ts';

import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../../../lib/logging';
import { useFeatureFlagsContext } from '../../../../providers/FeatureFlagsProvider/FeatureFlags.context';
import './MessageContainer.styles.scss';
import { useTranslation } from 'react-i18next';

/**
 * How close to the bottom edge counts as "asking for newer events".
 *
 * Only consulted when the thread was opened on an anchored window. Generous on
 * purpose: the backward path can afford an exact `scrollTop === 0` because the
 * user can always scroll further up, whereas the bottom is a hard stop and an
 * exact test there simply never fires on a trackpad.
 */
const FORWARD_PAGE_THRESHOLD_PX = 120;

export default function MessageContainer() {
  const { t } = useTranslation('chat/chat');
  const { isSmartLeadBotEnabled } = useFeatureFlagsContext();
  const { currentTheme } = useLayoutContext();
  const dropRef = useRef<HTMLDivElement>(null);
  const {
    selectedMessageContainer,
    loading,
    setLoading,
    messages,
    selectedChat,
    selectedChatLeadId,
    cursor,
    setCursor,
    setDroppedAttachments,
    setShowAllowTypesBanner,
    chatEvents,
    setChatEvents,
    groupParticipants,
    setGroupParticipants,
    autoRespondEnabled,
    setTakeConversationRequest,
    latestPage,
    leadAgentPaused,
  } = useChatContext();
  const { sendFileMessage, decryptSocketFile } = useChatActions();
  const {
    chatUpdatedEvent,
    chatInsertedEvent,
    chatContactInsertedEvent,
    chatContactUpdatedEvent,
    slbProcessingEvent,
    slbEscalatedEvent,
    slbStageAdvancedEvent,
    slbFunnelMovedEvent,
    reactionInsertedEvent,
    reactionRemovedEvent,
  } = useSocketsContext();
  const { selectedLeadForChatModal, focusedChatEvent, setFocusedChatEvent } =
    useModalsContext();
  const { parseErrors, castState, updateChatEvents } = chatUtils();
  const { addAlert } = useToastContext();
  const sectionRef = useRef<HTMLDivElement>(null);
  const topPadding = 150;
  // BUG-783: prevents overlapping parseAndInsertMessage calls for the same
  // event id from both passing the pre-await dedup guard. See
  // MessageContainer.functions.ts's createInFlightGuard doc comment.
  const insertInFlightGuardRef = useRef(createInFlightGuard());
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [pendingMessagesOnChat, setPendingMessagesOnChat] = useState<number>(0);
  // INT-1227: bumped when an album header stops waiting for its photos.
  const [albumClock, setAlbumClock] = useState(0);
  // AGI-500: Lead Agent's indicator. The hook owns the 15 s and 45 s stages and
  // ends the wait on a chat switch or when Lead Agent is switched off.
  const leadAgentIndicator = useLeadAgentIndicator({
    chatId: selectedChat?.id,
    agentEnabled: autoRespondEnabled,
  });
  // Tracks the last confirmed chat ID so websocket events are not dropped
  // during the brief window when selectedChat is null between chat reloads.
  const currentChatIdRef = useRef<string | null>(null);
  // Tracks the previous chat ID to detect genuine chat switches vs reloads.
  const prevChatIdRef = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastMessageRef = useRef<HTMLDivElement>(null);
  // SRE-3328. Live bubble nodes keyed by event id, so a message-search hit can
  // be scrolled to. Self-cleaning: React detaches every ref on a thread switch.
  const eventNodesRef = useRef(createEventNodeRegistry());
  // Timestamp until which the "stick to the bottom" timeouts below must stand
  // down. A search jump and the bottom-scroll would otherwise race, and the
  // 200 ms one would win — landing the user at the bottom anyway.
  const suppressBottomScrollUntilRef = useRef(0);
  const cancelFocusHighlightRef = useRef<(() => void) | null>(null);
  // SRE-3328, the `?around=` load. Three pieces of bookkeeping, all refs except
  // the two the render depends on:
  //   anchorAttemptedRef — the one event id this thread has already spent its
  //     single window fetch on. Without it the effect, which re-runs on every
  //     chatEvents change, would fetch again on each socket message.
  //   anchorLoadingRef — mirrors anchorLoading for the effect, which must not
  //     re-enter mid-flight and must not degrade while the answer is on its way.
  //   anchorEpoch — bumped once a window has been merged, purely so the focus
  //     effect is guaranteed to re-run and ask again. chatEvents.length alone
  //     cannot carry that: a 25-event window replacing a 25-event page leaves
  //     the length identical and the jump would never fire.
  const anchorAttemptedRef = useRef<string | null>(null);
  const anchorLoadingRef = useRef(false);
  const [anchorLoading, setAnchorLoading] = useState(false);
  const [anchorEpoch, setAnchorEpoch] = useState(0);
  // Set only by an anchored load: the thread then sits mid-history and has
  // unloaded events BELOW the viewport, which the normal newest-first thread
  // never does. Empty means "nothing newer to fetch" and disables forward
  // paging entirely, which is the state every ordinary thread stays in.
  const [afterCursor, setAfterCursor] = useState('');
  const { currentWorkspaceId: tiktokWorkspaceId } = useWorkspaceContext();
  const latestTikTokInbound = chatEvents
    .filter((event) => event.message && !event.message.fromMe)
    .reduce<(typeof chatEvents)[number] | undefined>(
      (latest, event) =>
        !latest || event.createdAt > latest.createdAt ? event : latest,
      undefined,
    );
  useTikTokReadMarker({
    workspaceId: tiktokWorkspaceId,
    leadId: selectedChat?.leadId,
    enabled: selectedChat?.integration === eChannelType.TIKTOK,
    ready: !loading && !anchorLoading && !afterCursor,
    latestInboundId: latestTikTokInbound?.id ?? '',
    scrollRef: sectionRef,
  });

  const afterCursorLoadingRef = useRef(false);

  // Stable by construction: EventBubble is React.memo'd, and a new identity
  // here would reattach every ref in the thread on each render.
  const registerEventNode = useCallback(
    (eventId: string, node: HTMLElement | null) => {
      eventNodesRef.current.register(eventId, node);
    },
    [],
  );

  const resolveEventNode = useCallback(
    (eventId: string) => eventNodesRef.current.resolve(eventId),
    [],
  );

  // SRE-3328: the in-chat search marks its matched words in these bubbles.
  useChatSearchHighlight({ resolveNode: resolveEventNode, thread: chatEvents });

  const bottomScrollIsSuppressed = () =>
    Date.now() < suppressBottomScrollUntilRef.current;

  /** Gives up on the jump and says so. The one degrade path, used by all of them. */
  const degradeJump = useCallback(() => {
    setFocusedChatEvent(null);
    addAlert({
      type: eAlertType.INFO,
      message: t('messageSearch.jumpNotLoaded'),
    });
  }, [setFocusedChatEvent, addAlert, t]);

  /**
   * Fetches the window centered on `eventId` and REPLACES the thread with it.
   *
   * Replacing, not merging, and that is the whole correctness argument. The
   * newest page already on screen and a window from three months back are two
   * disjoint stretches of the same thread with a hole between them; merging
   * them renders that hole as if the messages were adjacent, and leaves a
   * single `cursor` trying to describe two separate edges. The window is
   * self-consistent and carries a cursor for each of its own two edges, so the
   * thread stays contiguous and pageable both ways. What is discarded is one
   * page that scrolling back down re-fetches.
   *
   * @param eventId - The anchor to center on.
   */
  const loadAnchoredWindow = useCallback(
    async (eventId: string) => {
      const chatId = selectedChat?.id;

      if (!chatId) {
        degradeJump();
        return;
      }

      anchorLoadingRef.current = true;
      setAnchorLoading(true);

      try {
        const response = await ChatService.getEventsAround({ chatId, eventId });
        const events = response.data.events ?? [];

        // A bff that predates `around` ignores the key and answers 200 with the
        // newest page. Taking it would land the user at the bottom while the
        // code reported a successful jump — the silent fallback the endpoint
        // contract forbids. The anchor's presence is the only way to tell.
        if (!windowContainsAnchor(eventId, events)) {
          degradeJump();
          return;
        }

        setChatEvents(
          orderBy(
            uniqBy(
              mergeByHandle(events, selectedChat?.participants || []),
              'id',
            ),
            ['createdAt'],
            ['asc'],
          ),
        );

        // Each edge gets its own cursor. `cursorBefore` takes over the existing
        // scroll-up path unchanged; `afterCursor` switches on the scroll-down
        // path, which is inert on every thread that was not anchored.
        setCursor(
          response.data.hasMoreBefore ? (response.data.cursorBefore ?? '') : '',
        );
        setAfterCursor(
          response.data.hasMoreAfter ? (response.data.cursorAfter ?? '') : '',
        );

        // The thread is about to re-render around a mid-history window; the
        // "stick to the bottom" timers would undo it.
        suppressBottomScrollUntilRef.current = Date.now() + 2000;

        // Guarantees the focus effect runs again now that the anchor is in the
        // DOM, even when the window happens to be the same length as the page
        // it replaced.
        setAnchorEpoch((epoch) => epoch + 1);
      } catch (error) {
        // 404 (anchor gone) and 400 (anchor is another chat's) are permanent;
        // anything else did not complete. Neither is retried and both tell the
        // user the same thing, but they are classified rather than swallowed.
        classifyAnchorLoadError(error);
        degradeJump();
      } finally {
        anchorLoadingRef.current = false;
        setAnchorLoading(false);
      }
    },
    [
      selectedChat?.id,
      selectedChat?.participants,
      setChatEvents,
      setCursor,
      degradeJump,
    ],
  );

  /**
   * The one place that decides loaded-vs-not for a requested event id.
   *
   * It runs only once the thread on screen is the target's own (leadId match)
   * and its first page has rendered. On 'not-loaded' it spends exactly ONE
   * `?around=` fetch, then lets the re-render bring the anchor into the DOM and
   * asks again — the second pass takes the ordinary 'jumped' path, so the jump
   * and highlight below are unchanged either way. The event is never hunted
   * across cursor pages: that is unbounded work behind a spinner, and is what
   * `?around=` exists to replace.
   *
   * The target is consumed on every terminal outcome and only on a terminal
   * one; while the window is in flight it must stay set, or the second pass has
   * nothing to jump to.
   */
  useEffect(() => {
    if (!focusedChatEvent) return;
    if (loading) return;
    if (anchorLoadingRef.current) return;
    if (selectedLeadForChatModal?.id !== focusedChatEvent.leadId) return;
    // The thread ON SCREEN must be the target's own, which is a stricter
    // question than the one above. Clicking a result sets the requested lead
    // synchronously, while selectedChat and chatEvents still belong to the
    // conversation being navigated away from — and that conversation's events
    // are deliberately kept during the switch so the UI does not flash empty.
    // Matching only the requested lead therefore fires this against the
    // OUTGOING thread: it finds no node, spends the one target on it, and by
    // the time the real thread paints there is nothing left to jump to. With a
    // fetch behind it the same window is worse, because the anchor belongs to
    // another chat and bff-web correctly answers 400. selectedChatLeadId is
    // emptied when a load starts and only set beside its own selectedChat, so
    // it is the one value that cannot be ahead of what is rendered.
    if (selectedChatLeadId !== focusedChatEvent.leadId) return;
    // Same question, one level down: every chat of a lead shares its leadId, so
    // while an in-chat search switches channel the OUTGOING channel's thread
    // passes the lead check above for as long as the new chat is loading. A
    // producer that knows the chat says so, and then only that chat will do.
    if (focusedChatEvent.chatId && selectedChat?.id !== focusedChatEvent.chatId)
      return;
    if (!chatEvents.length) return;

    const { outcome, cancel } = focusChatEvent({
      eventId: focusedChatEvent.eventId,
      // An inbox result's snippet words, marked on the bubble it opens.
      terms: focusedChatEvent.terms,
      resolveNode: resolveEventNode,
      // The scroll port. A jump into a thread of images lands correctly and
      // then loses the anchor as they decode; measuring against this is how the
      // anchor is held there until the layout stops moving. See
      // watchAnchorStaysVisible.
      container: sectionRef.current,
      reducedMotion: prefersReducedMotion(),
    });

    if (outcome === 'not-loaded') {
      // One window per event id. The guard is on the id rather than a boolean
      // so a second search hit in the same open thread still gets its fetch.
      if (anchorAttemptedRef.current === focusedChatEvent.eventId) {
        degradeJump();
        return;
      }

      anchorAttemptedRef.current = focusedChatEvent.eventId;
      void loadAnchoredWindow(focusedChatEvent.eventId);
      return;
    }

    setFocusedChatEvent(null);
    suppressBottomScrollUntilRef.current = Date.now() + 1200;
    cancelFocusHighlightRef.current?.();
    cancelFocusHighlightRef.current = cancel;
  }, [
    focusedChatEvent,
    loading,
    selectedLeadForChatModal?.id,
    selectedChatLeadId,
    selectedChat?.id,
    chatEvents.length,
    anchorEpoch,
  ]);

  // A thread switch throws away everything the anchored load set up. Forward
  // paging in particular MUST reset: a stale afterCursor would page the new
  // thread forward from another conversation's event id.
  useEffect(() => {
    anchorAttemptedRef.current = null;
    setAfterCursor('');
  }, [selectedChat?.id]);

  /**
   * Swaps the newest page back in, as fetched by the chat hook's
   * `returnToLatest` when an in-chat search closes (SRE-3328).
   *
   * The inverse of loadAnchoredWindow, and for the same reason it REPLACES the
   * thread rather than merging: the anchored window and the newest page are two
   * disjoint stretches with a hole between them. It also undoes everything that
   * window set up — the forward cursor, the one-fetch guard, the highlight on the
   * last match and the bottom-scroll suppression — and lands on the newest
   * message, which is where the thread was before the search moved it.
   */
  useEffect(() => {
    if (!latestPage || latestPage.chatId !== selectedChat?.id) return;

    setChatEvents(
      orderBy(
        uniqBy(
          mergeByHandle(latestPage.events, selectedChat?.participants || []),
          'id',
        ),
        ['createdAt'],
        ['asc'],
      ),
    );
    setCursor(latestPage.cursor);
    setAfterCursor('');
    anchorAttemptedRef.current = null;
    cancelFocusHighlightRef.current?.();
    cancelFocusHighlightRef.current = null;
    suppressBottomScrollUntilRef.current = 0;

    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView();
    }, 0);

    return () => clearTimeout(timer);
  }, [latestPage?.epoch]);

  useEffect(() => {
    return () => {
      cancelFocusHighlightRef.current?.();
      cancelFocusHighlightRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const section = sectionRef.current;

      if (!section || loading) return;

      if (section.scrollTop === 0 && cursor) {
        getMoreMessages();
        return;
      }

      // Only ever true after an anchored load: an ordinary thread starts at its
      // newest event, so there is nothing below it and afterCursor stays empty.
      if (
        afterCursor &&
        section.scrollHeight - section.scrollTop - section.clientHeight <=
          FORWARD_PAGE_THRESHOLD_PX
      ) {
        void getNewerMessages();
      }
    };

    sectionRef.current?.addEventListener('scroll', handleScroll);
    return () => {
      sectionRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [sectionRef.current, loading, cursor, afterCursor]);

  // Clear chatEvents only when switching to a genuinely different chat.
  // When selectedChat goes null during a reload of the same chat we preserve
  // stale events so (a) the UI doesn't flash empty and (b) any websocket
  // bot-response that arrives during the null window isn't discarded.
  useEffect(() => {
    if (selectedChat?.id) {
      currentChatIdRef.current = selectedChat.id;
      if (prevChatIdRef.current && prevChatIdRef.current !== selectedChat.id) {
        // Genuinely switched to a different chat — clear stale events immediately.
        setChatEvents([]);
      }
      prevChatIdRef.current = selectedChat.id;
    }
  }, [selectedChat?.id]);

  useEffect(() => {
    setChatEvents((prevEvents) => {
      if (!messages?.events.length) return [];

      const newEvents = messages?.events || [];
      const sortedEvents = orderBy(
        [...prevEvents, ...newEvents],
        ['createdAt'],
        ['asc'],
      );
      const uniqueEvents = uniqBy(sortedEvents, 'id');
      // Guard: skip mergeByHandle when participants is empty (selectedChat not
      // yet loaded or a TikTok 1:1 with no group participants). Running the
      // merge loop with an empty participants array is a no-op that mutates
      // event objects in-place without resolving any names; skipping it avoids
      // stale participant.name = '' writes that would persist until the next
      // selectedChat?.id change triggers a re-merge with the real participants.
      const participants = selectedChat?.participants;
      const mergedEvents =
        participants && participants.length > 0
          ? mergeByHandle(uniqueEvents, participants)
          : uniqueEvents;

      return mergedEvents;
    });
    // Scroll to the bottom of the chat after a new message is added to the DOM,
    // unless a search jump has just claimed the scroll position (SRE-3328).
    setTimeout(() => {
      if (bottomScrollIsSuppressed()) return;
      if (messagesEndRef.current && !showScrollButton) {
        messagesEndRef.current.scrollIntoView();
      }
    }, 200);
  }, [selectedChat?.id, messages?.events.length]);

  useEffect(() => {
    if (!sectionRef.current) return;
    sectionRef.current.addEventListener('scroll', () => {
      if (
        sectionRef.current &&
        sectionRef.current.scrollHeight -
          sectionRef.current.scrollTop -
          topPadding >=
          sectionRef.current.clientHeight
      ) {
        setShowScrollButton(true);
      } else {
        setPendingMessagesOnChat(0);
        setShowScrollButton(false);
      }
    });
  }, [sectionRef]);

  useEffect(() => {
    if (chatEvents.length > 0) {
      const lastEvent = chatEvents[chatEvents.length - 1];
      if (lastEvent.type === EventType.LEADSALES_AI) {
        setTimeout(() => {
          if (bottomScrollIsSuppressed()) return;
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
  }, [chatEvents.length]);

  const getMoreMessages = async () => {
    try {
      if (!selectedChat?.id || !cursor) return;
      const response = await ChatService.getNextPage({
        chatId: selectedChat?.id,
        cursor: cursor,
      });
      setCursor(response.data.cursor);

      if (response.data.events.length === 0) return;

      if (response.data.events.length) {
        setChatEvents((prevEvents) => {
          const merged = mergeByHandle(
            [...prevEvents, ...response.data.events],
            selectedChat?.participants || [],
          );
          return orderBy(uniqBy(merged, 'id'), ['createdAt'], ['asc']);
        });
      }

      // Scroll to the previous top message after new messages are added to the DOM
      setTimeout(() => {
        if (lastMessageRef.current) {
          lastMessageRef.current.scrollIntoView();
        }
      }, 0);
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message ||
          'Ocurrió un error al obtener la información del chat',
      });
    }
  };

  /**
   * Pages the thread FORWARD, toward newer events.
   *
   * The counterpart to getMoreMessages, and only reachable after an anchored
   * load: it is what stops the user being stranded at the bottom edge of a
   * window that sits in the middle of the conversation.
   *
   * Unlike the backward path it does not restore scroll position — appending
   * below the viewport does not move what the user is looking at, so the
   * browser's own anchoring is already correct.
   */
  const getNewerMessages = async () => {
    if (afterCursorLoadingRef.current) return;
    if (!selectedChat?.id || !afterCursor) return;

    afterCursorLoadingRef.current = true;

    try {
      const response = await ChatService.getNextPage({
        chatId: selectedChat.id,
        cursor: afterCursor,
        direction: 'forward',
      });

      // Exhausted is the normal end state here, and it must latch: leaving the
      // cursor set would re-fire this on every further scroll at the bottom.
      setAfterCursor(
        response.data.hasMoreAfter ? (response.data.cursorAfter ?? '') : '',
      );

      if (!response.data.events?.length) return;

      setChatEvents((prevEvents) => {
        const merged = mergeByHandle(
          [...prevEvents, ...response.data.events],
          selectedChat?.participants || [],
        );
        return orderBy(uniqBy(merged, 'id'), ['createdAt'], ['asc']);
      });
    } catch (error) {
      addAlert({
        type: eAlertType.ERROR,
        message:
          (error as Error).message ||
          'Ocurrió un error al obtener la información del chat',
      });
    } finally {
      afterCursorLoadingRef.current = false;
    }
  };

  const chatContainerClass =
    currentTheme === 'dark'
      ? 'MessageContainer__ChatDark'
      : 'MessageContainer__Chat';

  function handleDragOver(event: React.DragEvent) {
    event.preventDefault();

    if (dropRef.current) {
      dropRef.current.classList.add('drag-over');
    }
  }

  function handleDragLeave() {
    if (dropRef.current) {
      dropRef.current.classList.remove('drag-over');
    }
  }

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setLoading(true);
    if (dropRef.current) {
      dropRef.current.classList.remove('drag-over');
    }

    if (
      event.dataTransfer.files &&
      selectedLeadForChatModal?.chat?.integration
    ) {
      const files = event.dataTransfer.files;
      // Captured before the first await. Uploading (and, for HEIC, decoding) can
      // run for tens of seconds, and the agent may switch leads meanwhile — the
      // attachment must remember which conversation it was meant for.
      const originLeadId = selectedChat?.leadId ?? selectedLeadForChatModal?.id;
      const attachments: tChatAttachment[] = [];
      const allFilesValid = Array.from(files).every((file) =>
        isAcceptableForChannel(
          file,
          selectedLeadForChatModal?.chat?.integration,
        ),
      );

      /* Check if there are MOV files - this is temporary until we have a better solution in backend */
      for (const file of files) {
        const isMov = file.name.toLowerCase().endsWith('.mov');

        if (isMov) {
          addAlert({
            type: eAlertType.WARNING,
            message: t('driveFilesAction.fileTypeNotSupported', {
              fileName: file.name,
            }),
          });
          return;
        }
      }

      if (files?.length) {
        const channel = selectedLeadForChatModal?.chat?.integration;
        for (const file of Array.from(files)) {
          if (isEmptyFile(file)) {
            addAlert({
              type: eAlertType.WARNING,
              message: t('driveFilesAction.emptyFile', { fileName: file.name }),
            });
            setLoading(false);
            return;
          }

          const { maxSize, humanLimit } = getMaxFileSizeForChannel(
            channel,
            file,
          );

          // Oversize but compressible files (images incl. HEIC, videos) are let
          // through: sendFileMessage transcodes/compresses to the channel limit
          // before upload. This mirrors the file picker's handleFileChange gate.
          if (
            file.size > maxSize &&
            !MediaCompressionService.canCompressFile(file)
          ) {
            const message = t('driveFilesAction.fileSizeExceeded', {
              fileName: file.name,
              maxSize: humanLimit,
            });

            addAlert({
              type: eAlertType.WARNING,
              message,
            });
            return;
          }
        }

        const uploadHolder = {};
        try {
          for (let i = 0; i < files.length; i++) {
            let poster = '';
            if (isVideo(files[i].type)) {
              poster = await generateVideoPoster(files[i]);
            }

            let source: string;
            // Prefer the uploaded file's derived metadata so a transcoded HEIC
            // shows as image/jpeg / *.jpg in the tray (matches the sent bytes).
            let sentType = files[i].type;
            let sentName = files[i].name;
            try {
              const fileFromS3 = (await sendFileMessage(
                files[i],
                false,
                true,
              )) as tAttachment;

              source =
                fileFromS3 && fileFromS3.url
                  ? fileFromS3.url
                  : createHeldBlobUrl(files[i], uploadHolder);
              sentType = fileFromS3?.mimeType ?? sentType;
              sentName = fileFromS3?.filename ?? sentName;
            } catch (error) {
              LLog.error(
                {
                  component: LOG_COMPONENTS.CHAT,
                  method: 'MessageContainer.handleDrop',
                  operation: LOG_OPERATIONS.ERROR,
                },
                `Failed to upload file: ${files[i].name}`,
                error as unknown as Error,
              );
              source = createHeldBlobUrl(files[i], uploadHolder);
            }

            attachments.push({
              file: files[i],
              type: sentType,
              src: source,
              name: sentName,
              size: files[i].size,
              poster,
              leadId: originLeadId,
            });
          }

          setDroppedAttachments((prevAttachments) => [
            ...prevAttachments,
            ...attachments,
          ]);
        } finally {
          retainDraftBlobAttachments(originLeadId, attachments);
          releaseBlobUrlHolder(uploadHolder);
        }
      }

      if (!allFilesValid) {
        setShowAllowTypesBanner(true);
      }
    }
    setLoading(false);
  };

  chatUpdatedEvent.useSubscription((event: tChatEventSocket) => {
    if (event.chat_id !== selectedChat?.id) return;

    parseAndUpdateChat(event);
  });

  chatInsertedEvent.useSubscription((event: tChatEventSocket) => {
    if (event.chat_id !== selectedChat?.id) return;
    if (
      (selectedChat.integration === eChannelType.INSTAGRAM ||
        selectedChat.integration === eChannelType.MESSENGER) &&
      event.creation_reason === 'INSERTED_MESSAGE_CREATION_REASON'
    )
      return;

    parseAndInsertMessage(event);
  });

  chatContactInsertedEvent.useSubscription((event: tChatContactEvent) => {
    const participants = groupParticipants || [];
    const existingParticipant = participants.find(
      (p: tParticipant) => p.handle === event.handle,
    );
    if (existingParticipant) return;
    participants.push(event);
    setGroupParticipants(participants);
  });

  // Show the indicator only when chatbot-service confirms it is actually
  // processing a message for this chat, which eliminates false positives. It
  // stays until Lead Agent replies, hands off, or someone takes the chat.
  slbProcessingEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChat?.id) return;
    // A billing-paused workspace gets no reply: chatbot-service skips the
    // dispatch after its processing event, so there is no wait to show.
    if (leadAgentPaused.paused) return;
    leadAgentIndicator.start();
  });

  // A pause that arrives during a wait ends it instead of letting it run to
  // "no respondió".
  useEffect(() => {
    if (leadAgentPaused.paused) leadAgentIndicator.stop();
  }, [leadAgentPaused.paused]);

  slbEscalatedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChat?.id) return;
    leadAgentIndicator.stop();
    addAlert({
      type: eAlertType.INFO,
      message: t('smartLeadbot.handoffOccurred'),
    });

    setChatEvents((prev) => [
      ...prev,
      {
        agendaEvent: null,
        chatID: event.chat_id,
        content: {
          text: t('smartLeadbot.systemHandoff'),
          attachments: null,
          linkPreview: null,
          quotedEventID: null,
        },
        createdAt: new Date().toISOString(),
        createdBy: 'system',
        deletedAt: '',
        followUp: null,
        id: `system-handoff-${Date.now()}`,
        message: null,
        note: null,
        system: { subtype: 'handoff' },
        type: EventType.SYSTEM,
        updatedAt: new Date().toISOString(),
      },
    ]);
  });

  slbStageAdvancedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChat?.id) return;
    const stageName = selectedChat?.lead?.stages?.find(
      (s) => s.id === event.next_stage_id,
    )?.name;
    if (!stageName) return;

    setChatEvents((prev) => [
      ...prev,
      {
        agendaEvent: null,
        chatID: event.chat_id,
        content: {
          text: t('smartLeadbot.systemStageAdvance', { stage: stageName }),
          attachments: null,
          linkPreview: null,
          quotedEventID: null,
        },
        createdAt: new Date().toISOString(),
        createdBy: 'system',
        deletedAt: '',
        followUp: null,
        id: `system-stage-${Date.now()}`,
        message: null,
        note: null,
        system: { subtype: 'stage-advance' },
        type: EventType.SYSTEM,
        updatedAt: new Date().toISOString(),
      },
    ]);
  });

  slbFunnelMovedEvent.useSubscription((event) => {
    if (!isSmartLeadBotEnabled || event.chat_id !== selectedChat?.id) return;

    // Build the human-readable body once here (same i18n key the bubble uses)
    // as an accessible fallback on content.text, and carry the structured
    // funnel/stage identifiers on system.funnelMove so the in-chat deep-link can
    // route to the destination funnel. Tolerate a partial payload — absent names
    // degrade the text, absent to_funnel.uuid just hides the deep-link.
    const funnelName = event.to_funnel?.name;
    const stageName = event.to_stage?.name;
    const bodyText =
      funnelName && stageName
        ? t('smartLeadbot.systemFunnelMove', {
            funnel: funnelName,
            stage: stageName,
          })
        : funnelName
          ? t('smartLeadbot.systemFunnelMoveNoStage', { funnel: funnelName })
          : t('smartLeadbot.funnelMoveGeneric');

    // Reuse the server-generated note_id as the synthesized event id so that if
    // the durable note (persisted by messaging) also echoes live via
    // chat_event.inserted, uniqBy('id') collapses the pair instead of showing
    // two lilac cards. Guard the direct append too, in case the note echo landed
    // first (that path does not re-run uniqBy).
    const syntheticId = event.note_id || `system-funnel-move-${Date.now()}`;
    setChatEvents((prev) => {
      if (event.note_id && prev.some((e) => e.id === event.note_id))
        return prev;
      return [
        ...prev,
        {
          agendaEvent: null,
          chatID: event.chat_id,
          content: {
            text: bodyText,
            attachments: null,
            linkPreview: null,
            quotedEventID: null,
          },
          createdAt: event.moved_at || new Date().toISOString(),
          createdBy: 'system',
          deletedAt: '',
          followUp: null,
          id: syntheticId,
          message: null,
          note: null,
          system: {
            subtype: 'funnel-move',
            funnelMove: {
              reason: event.reason,
              fromFunnel: event.from_funnel,
              toFunnel: event.to_funnel,
              toStage: event.to_stage,
            },
          },
          type: EventType.SYSTEM,
          updatedAt: event.moved_at || new Date().toISOString(),
        },
      ];
    });
  });

  chatContactUpdatedEvent.useSubscription((event: tChatContactEvent) => {
    const participants = groupParticipants || [];
    const updateParticipants = participants.map((participant: tParticipant) => {
      if (participant.handle === event.handle) {
        return {
          ...participant,
          name: event.name,
        };
      }
      return participant;
    });
    setGroupParticipants(updateParticipants);
  });

  reactionInsertedEvent.useSubscription((event: tReactionSocket) => {
    const effectiveChatId = selectedChat?.id ?? currentChatIdRef.current;
    if (!effectiveChatId || event.chat_id !== effectiveChatId) return;
    if (!event.emoji) return;
    const emoji = event.emoji;
    setChatEvents((prev) => {
      const idx = prev.findIndex((evt) => evt.id === event.chat_event_id);
      if (idx === -1) return prev;
      const target = prev[idx];
      const reactions = target.reactions ?? [];
      // Skip if optimistic update already applied the same emoji (dedup)
      const existing = reactions.find((r) => r.fromMe === event.from_me);
      if (
        existing?.emoji === emoji &&
        (existing.emojiUrl ?? null) === (event.emoji_url ?? null)
      )
        return prev;
      const withoutSameSide = reactions.filter(
        (r) => r.fromMe !== event.from_me,
      );
      const next = [...prev];
      next[idx] = {
        ...target,
        reactions: [
          ...withoutSameSide,
          {
            id: 0,
            chatEventId: event.chat_event_id,
            providerId: '',
            fromMe: event.from_me,
            emoji,
            emojiUrl: event.emoji_url,
            createdBy: null,
            createdAt: event.updated_at,
            updatedAt: event.updated_at,
          },
        ],
      };
      return next;
    });
  });

  reactionRemovedEvent.useSubscription((event: tReactionSocket) => {
    const effectiveChatId = selectedChat?.id ?? currentChatIdRef.current;
    if (!effectiveChatId || event.chat_id !== effectiveChatId) return;
    setChatEvents((prev) => {
      const idx = prev.findIndex((evt) => evt.id === event.chat_event_id);
      if (idx === -1) return prev;
      const target = prev[idx];
      const next = [...prev];
      next[idx] = {
        ...target,
        reactions: (target.reactions ?? []).filter(
          (r) => r.fromMe !== event.from_me,
        ),
      };
      return next;
    });
  });

  const parseAndUpdateChat = (event: tChatEventSocket) => {
    const effectiveChatId = selectedChat?.id ?? currentChatIdRef.current;
    if (!effectiveChatId || effectiveChatId !== event.chat_id) return;
    setChatEvents((prevEvents) =>
      prevEvents.map((chatEvent) =>
        chatEvent.id === event.id
          ? updateChatEvents(event, chatEvent)
          : chatEvent,
      ),
    );
  };

  const parseAndInsertMessage = async (event: tChatEventSocket) => {
    // Use the ref so that events arriving during the selectedChat null window
    // (e.g. the bot reply arriving while the chat is mid-reload) are not dropped.
    const effectiveChatId = selectedChat?.id ?? currentChatIdRef.current;
    if (!effectiveChatId || effectiveChatId !== event.chat_id) return;

    // BUG-783: this function awaits createNewChatEvent before the dedup
    // guard below, so a second overlapping call for the same event id could
    // otherwise pass that guard too. Reject the overlap synchronously,
    // before any async work starts, rather than relying only on the
    // post-await checks.
    if (!insertInFlightGuardRef.current.tryStart(event.id)) {
      return;
    }

    try {
      const newChatEvent = await createNewChatEvent(event);

      // Dedup: skip all side effects if this message already exists in state.
      // The setChatEvents callback below has its own guard for race conditions,
      // but this early check prevents counter drift and unnecessary re-renders.
      if (chatEvents?.some((msg) => msg.id === newChatEvent.id)) {
        return;
      }

      if (
        isSmartLeadBotEnabled &&
        newChatEvent?.message?.fromMe &&
        newChatEvent.createdBy === 'smart_leadbot'
      ) {
        // Lead Agent replied: the indicator's Done, announced once.
        leadAgentIndicator.done();
      }

      if (newChatEvent?.message?.fromMe && pendingMessagesOnChat > 0) {
        setPendingMessagesOnChat(0);
      } else {
        setPendingMessagesOnChat(pendingMessagesOnChat + 1);
      }

      const participants = groupParticipants || [];

      setChatEvents((prevMessages) => {
        if (!prevMessages) {
          return [newChatEvent];
        }
        if (prevMessages.some((msg) => msg.id === newChatEvent.id)) {
          return prevMessages;
        }
        return [
          ...prevMessages,
          ...mergeByHandle([newChatEvent], participants),
        ];
      });

      // Scroll to the bottom of the chat after a new message is added to the DOM
      setTimeout(() => {
        if (messagesEndRef.current && !showScrollButton) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 0);
    } finally {
      insertInFlightGuardRef.current.finish(event.id);
    }
  };

  async function createNewChatEvent(event: tChatEventSocket) {
    let attachments: tAttachment[] = [];

    if (event.content.attachments && event.content.attachments.length > 0) {
      attachments = await Promise.all(
        event.content.attachments.map(async (attachment) => {
          let url = attachment?.url;

          if (
            attachment?.decryption?.keys?.iv !== '' &&
            attachment?.decryption?.keys?.iv !== undefined
          ) {
            url = (await decryptSocketFile(attachment)) || '';
          }

          // Wire-shape → `tAttachment` translation lives in a pure helper so
          // it can be regression-tested in isolation (INT-929 — SHARE_POST
          // MUST route to MediaType.SHARE_POST before the default branch).
          return buildDecryptedAttachment(attachment, url);
        }),
      );
    }

    // The allowlist itself lives in buildEventContent so every key it must
    // carry is locked by MessageContainer.functions.test.ts — a key missing
    // there is the silent-drop bug class (link_preview / interactive /
    // ad_referral / carousel), see
    // docs/solutions/logic-errors/websocket-event-payload-regression-defensive-reducer.md.
    const content: tContent = buildEventContent({ event, attachments });

    // Every branch below assigns an `EventType` member (the switch only
    // reads `event.type`, an `eChatEventTypes`, as the discriminant).
    let eventType: EventType;

    switch (event.type) {
      case eChatEventTypes.MESSAGE:
        eventType = EventType.MESSAGE;
        break;
      case eChatEventTypes.TEMPLATE:
        eventType = EventType.TEMPLATE;
        break;
      case eChatEventTypes.NOTE:
        eventType = EventType.NOTE;
        break;
      case eChatEventTypes.FOLLOW_UP:
        eventType = EventType.FOLLOW_UP;
        break;
      default:
        eventType = EventType.MESSAGE;
        break;
    }
    let agendaEvent: tCalendarEvent | null = null;

    if (event.type === eChatEventTypes.CALENDAR_EVENT) {
      eventType = EventType.CALENDAR_EVENT;

      if (event.calendar_event_id) {
        const agendaEventResponse = await AgendaService.getEventByID(
          event.calendar_event_id,
        );
        const parsedDuration = getDuration(
          agendaEventResponse?.start_date || '',
          agendaEventResponse?.end_date || '',
        );

        if (agendaEventResponse) {
          agendaEvent = {
            createdBy: event.created_by,
            duration: convertDurationToMinutes(parsedDuration.duration),
            eventType: convertToCalendarEventType(
              agendaEventResponse.event_type,
            ),
            id: agendaEventResponse.id,
            name: agendaEventResponse.name,
            startDate: agendaEventResponse.start_date,
            note: agendaEventResponse.note,
          };
        }
      }
    }

    const message: tMessage = {
      error: parseErrors(event.errors),
      fromMe: event?.content?.from_me || false,
      isFavorite: event.is_favorite || false,
      isForwarded: event.content.is_forwarded || false,
      participant: {
        handle:
          event?.participant?.handle ||
          (selectedLeadForChatModal && selectedLeadForChatModal.handle) ||
          '',
        name: event?.participant?.name || '',
      },
      state: castState(event.state),
    };

    return buildChatEventFromSocketEvent({
      event,
      chatID: selectedChat?.id || '',
      content,
      message,
      type: eventType,
      agendaEvent,
    });
  }

  // AGI-500: "Tomar conversación" runs the take-over the composer already owns
  // (useAutoRespondToggle, with its survey); the thread only asks for it. The
  // indicator leaves at once: the person answered the wait.
  const takeConversation = () => {
    if (selectedChat?.id)
      setTakeConversationRequest({ chatId: selectedChat.id });
    leadAgentIndicator.stop();
  };

  // INT-1505: a scheduled card whose sent message is in the thread gives way
  // to it, and the message carries the card's "programado por" legend.
  const scheduledPairs = useMemo(
    () => pairScheduledMessages(chatEvents),
    [chatEvents],
  );

  // INT-1227: photos sent together render as one album bubble.
  const albums = useMemo(
    () =>
      groupChatAlbums(chatEvents, {
        channel: selectedChat?.integration,
        now: Date.now(),
        inboundGapMs: VITE_CHAT_ALBUM_INBOUND_GAP_SECONDS * 1000,
        outboundGapMs: VITE_CHAT_ALBUM_OUTBOUND_GAP_SECONDS * 1000,
      }),
    [chatEvents, selectedChat?.integration, albumClock],
  );

  // A header still waiting for its photos: group again once it stops waiting,
  // so it shows its card if no photo came.
  useEffect(() => {
    if (albums.recheckAt === undefined) return;
    const timer = setTimeout(
      () => setAlbumClock((tick) => tick + 1),
      Math.max(0, albums.recheckAt - Date.now()) + 50,
    );
    return () => clearTimeout(timer);
  }, [albums.recheckAt, albumClock]);

  // The thread renders in time order: the socket appends a late event at the
  // end, and the day separators and runs must follow what is on screen.
  // LEA-50 (U14): the lead's results join the thread as cards, from the close
  // summary the bar and the panel already read: no request per message. Only
  // inside the loaded range, so a card never jumps when older messages load.
  const leadClose = useLeadClose();
  const closeResults =
    leadClose.enabled && selectedLeadForChatModal?.id
      ? leadClose.summaryOf(selectedLeadForChatModal.id).data?.results
      : undefined;
  const orderedEvents = useMemo(() => {
    if (!closeResults?.length) return orderThreadEvents(chatEvents);
    const thread = orderThreadEvents(chatEvents);
    const range = {
      from: cursor ? (thread[0]?.createdAt ?? null) : null,
      to: afterCursor ? (thread[thread.length - 1]?.createdAt ?? null) : null,
    };
    return orderThreadEvents([
      ...chatEvents,
      ...leadCloseChatEvents(closeResults, range, selectedChat?.id ?? ''),
    ]);
  }, [chatEvents, closeResults, cursor, afterCursor, selectedChat?.id]);

  // A save made here brings its card and the stage suggestion into view: they
  // do not change the message count the thread's own scroll follows.
  const ownSaveId =
    leadClose.lastSave?.surface === 'chat' &&
    leadClose.lastSave.leadId === selectedLeadForChatModal?.id
      ? leadClose.lastSave.id
      : null;
  useEffect(() => {
    if (!ownSaveId) return;
    const frame = requestAnimationFrame(() =>
      messagesEndRef.current?.scrollIntoView({ block: 'end' }),
    );
    return () => cancelAnimationFrame(frame);
  }, [ownSaveId]);

  // Where each day starts (a separator goes before it) and where each run of
  // bubbles from one sender starts, over the items the thread renders.
  const bubbleLayout = useMemo(
    () =>
      buildBubbleLayout(orderedEvents, {
        isHidden: (event) =>
          scheduledPairs.sentByFollowUpId.has(event.id) ||
          albums.hiddenIds.has(event.id),
        isAlbumAnchor: (event) => albums.albumByAnchorId.has(event.id),
      }),
    [orderedEvents, scheduledPairs, albums],
  );
  const bubbleDate = useBubbleDate();

  if (selectedMessageContainer === ChatMessageContainerEnum.CHAT) {
    return (
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        ref={dropRef}
        className="h-full relative"
        data-channel={selectedChat?.integration ?? eChannelType.WHATSAPP_WEB}
      >
        <EventBubbleProvider>
          {!selectedChat || (loading && !chatEvents.length) ? (
            <div className="w-full h-full">
              <Skeleton className="w-full h-full" />
            </div>
          ) : (
            <>
              <section
                className={chatContainerClass}
                ref={sectionRef}
                translate="no"
              >
                {cursor.length && chatEvents.length ? (
                  <div className="flex justify-center items-center w-full">
                    <Loading className="text-primary" />
                  </div>
                ) : null}
                {/* Spacer that fills unused vertical space so messages anchor to the bottom */}
                <div className="flex-1" />
                {orderedEvents.map((message) => {
                  if (scheduledPairs.sentByFollowUpId.has(message.id))
                    return null;
                  if (albums.hiddenIds.has(message.id)) return null;

                  const layout = bubbleLayout.get(message.id);
                  const daySeparator = layout?.startsDay ? (
                    <DaySeparator
                      label={bubbleDate.dayLabel(message.createdAt)}
                      dayKey={layout.dayKey}
                    />
                  ) : null;

                  const album = albums.albumByAnchorId.get(message.id);
                  if (album) {
                    return (
                      <Fragment key={`album-${message.id}`}>
                        {daySeparator}
                        <AlbumBubble
                          album={album}
                          innerRef={lastMessageRef}
                          registerNode={registerEventNode}
                          scheduledOrigin={album
                            .map((photo) =>
                              scheduledPairs.followUpBySentId.get(photo.id),
                            )
                            .find(Boolean)}
                        />
                      </Fragment>
                    );
                  }

                  return (
                    <Fragment key={message.id}>
                      {daySeparator}
                      <EventBubble
                        event={message}
                        innerRef={lastMessageRef}
                        registerNode={registerEventNode}
                        scheduledOrigin={scheduledPairs.followUpBySentId.get(
                          message.id,
                        )}
                        groupStart={layout?.groupStart ?? true}
                      />
                    </Fragment>
                  );
                })}
                {/* The mirror of the top spinner, and only ever shown on a thread
                  opened at an anchored window: an ordinary thread has nothing
                  below its newest event. */}
                {afterCursor && chatEvents.length ? (
                  <div className="flex justify-center items-center w-full">
                    <Loading className="text-primary" />
                  </div>
                ) : null}
                {/* LEA-50 (U14): after a save, the stage it suggests (newest edge only). */}
                {leadClose.enabled &&
                selectedLeadForChatModal?.id &&
                !afterCursor ? (
                  <LeadCloseSuggestion
                    leadId={selectedLeadForChatModal.id}
                    currentStageId={selectedLeadForChatModal.stage?.id}
                    currentStageName={selectedLeadForChatModal.stage?.name}
                    funnelId={selectedChat?.lead?.funnelId}
                  />
                ) : null}
                {/* Room under the last message, as in WhatsApp; every scroll to
                the bottom lands on this marker, so the room always shows. */}
                <div
                  ref={messagesEndRef}
                  className="MessageContainer__End"
                  data-messages-end="true"
                ></div>
              </section>
              {/* Mounts with the scroller, so it always listens to the live one. */}
              <FloatingDayChip scrollerRef={sectionRef} />
            </>
          )}
          {/* A jump to an old message costs a round trip. Without something on
              screen for it the click reads as dead, and the thread underneath
              still shows the bottom — which looks like the jump ran and landed
              in the wrong place. */}
          {anchorLoading ? (
            <div
              className="MessageContainer__AnchorLoading"
              data-testid="chat-anchor-loading"
              role="status"
              aria-live="polite"
            >
              <Loading className="text-primary" size="lg" />
              <p className="MessageContainer__AnchorLoading__Label">
                {t('messageSearch.jumpLoading')}
              </p>
            </div>
          ) : null}
          {isSmartLeadBotEnabled ? (
            <BotThinkingBubble
              stage={leadAgentIndicator.stage}
              replied={leadAgentIndicator.replied}
              onTakeConversation={takeConversation}
            />
          ) : null}
          {showScrollButton ? (
            <ScrollButton
              messagesEndRef={messagesEndRef}
              pendingMessagesOnChat={pendingMessagesOnChat}
            />
          ) : null}
        </EventBubbleProvider>
      </div>
    );
  }

  return (
    <section className="MessageContainer__Record">
      <div className="MessageContainer__Record__Right">
        <Records />
      </div>
    </section>
  );
}
